import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../design/design-system";
import Text from "../atoms/Text";
import Button from "../atoms/Button";
import { log, LogLevel } from "utils/LogUtil";

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const FormField = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin-bottom: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 8px;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  }

  &::placeholder {
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }
`;

const CharacterCount = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  text-align: right;
  margin-top: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

interface MemoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (memo: string) => void;
  title: string;
  initialMemo?: string | null;
  maxLength?: number;
}

const MemoEditModal: React.FC<MemoEditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialMemo = "",
  maxLength = 500,
}) => {
  const [memo, setMemo] = useState(initialMemo || "");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = () => {
    const trimmedMemo = memo.trim();
    onSubmit(trimmedMemo);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      setMemo(newValue);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 모달이 열릴 때 초기값 설정 및 포커스 설정
  useEffect(() => {
    if (isOpen) {
      setMemo(initialMemo || "");
      // DOM이 업데이트된 후 포커스 설정
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 100);
    }
  }, [isOpen, initialMemo]);

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Text textType="H2" color={COLOR_SYSTEM.get("Gray")?.[800]}>
            {title}
          </Text>
        </ModalHeader>

        <FormField>
          <Label>메모 내용</Label>
          <TextArea
            ref={textAreaRef}
            value={memo}
            onChange={handleTextChange}
            placeholder="메모를 입력해주세요..."
            autoFocus
          />
          <CharacterCount>
            {memo.length} / {maxLength}
          </CharacterCount>
        </FormField>

        <ButtonContainer>
          <Button
            buttonType="button"
            type="Text"
            variant="Outlined"
            size="Medium"
            color="Tertiary"
            onClick={onClose}
          >
            취소
          </Button>
          <Button
            buttonType="button"
            type="Text"
            variant="Contained"
            size="Medium"
            color="Primary"
            onClick={handleSubmit}
          >
            저장
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MemoEditModal;